import React, { useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import {
  AccountBalanceRounded,
  AddRounded,
  ArrowBackRounded,
  BadgeRounded,
  ArrowForwardRounded,
  CheckCircleRounded,
  CheckRounded,
  VerifiedUserRounded,
  DeleteOutlineRounded,
  FactCheckRounded,
  DescriptionRounded,
  InfoOutlined,
  LocationOnRounded,
  PersonAddAltRounded,
  PersonRounded,
  SchoolRounded,
  ShieldRounded,
  WorkRounded,
} from "@mui/icons-material";
import "./CareerFDCApplication.css";

const SERVER_URL =
  process.env.REACT_APP_SERVER_SCRIPT_HOST ||
  process.env.REACT_APP_UNIVERSE_API_URL ||
  "http://localhost:5000/api/v1/universe-inventory";

const submitFDCCareerApplication = async (payload) => {
  const response = await axios.post(
    `${SERVER_URL.replace(/\/$/, "")}/career/application/fdc`,
    payload,
  );

  return response.data;
};

const NIGERIAN_STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "Federal Capital Territory",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
];

const steps = [
  { id: 1, title: "Personal & address", icon: PersonRounded },
  { id: 2, title: "Emergency & kin", icon: LocationOnRounded },
  { id: 3, title: "Education", icon: SchoolRounded },
  { id: 4, title: "Documents", icon: AccountBalanceRounded },
  { id: 5, title: "Work location", icon: LocationOnRounded },
  { id: 6, title: "Review", icon: CheckCircleRounded },
];

const emptyEducation = () => ({
  level: "",
  institution: "",
  qualification: "",
  fieldOfStudy: "",
  startYear: "",
  endYear: "",
});

function StateInput({ value, onChange }) {
  const known = NIGERIAN_STATES.includes(value);
  return (
    <div className="fdcRelationship">
      <select
        value={!value ? "" : known ? value : "Other"}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select Nigerian state</option>
        {NIGERIAN_STATES.map((state) => (
          <option value={state} key={state}>
            {state}
          </option>
        ))}
        <option value="Other">Other / outside Nigeria</option>
      </select>
      {(value === "Other" || (value && !known)) && (
        <input
          placeholder="Specify state or province"
          value={value === "Other" ? "" : value}
          onChange={(event) => onChange(event.target.value || "Other")}
        />
      )}
    </div>
  );
}

const RELATIONSHIPS = [
  "Father",
  "Mother",
  "Husband",
  "Wife",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
  "Uncle",
  "Aunt",
  "Cousin",
  "Grandfather",
  "Grandmother",
  "Nephew",
  "Niece",
  "Guardian",
  "Other",
];

function RelationshipInput({ value, onChange }) {
  const known = RELATIONSHIPS.includes(value);
  const isOther = Boolean(value) && !known;
  return (
    <div className="fdcRelationship">
      <select
        value={isOther ? "Other" : value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select relationship</option>
        {RELATIONSHIPS.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
      {(value === "Other" || isOther) && (
        <input
          aria-label="Specify relationship"
          placeholder="Specify relationship"
          value={value === "Other" ? "" : value}
          onChange={(event) => onChange(event.target.value || "Other")}
        />
      )}
    </div>
  );
}

function DocumentUpload({
  label,
  value,
  onChange,
  accept,
  multiple = false,
  optional = false,
}) {
  const inputId = React.useId();
  const [preview, setPreview] = React.useState(null);
  React.useEffect(() => {
    if (!preview) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPreview(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [preview]);
  const files = Array.isArray(value) ? value : value ? [value] : [];
  const previews = React.useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [value],
  );
  React.useEffect(
    () => () => previews.forEach((item) => URL.revokeObjectURL(item.url)),
    [previews],
  );
  const invalid = files.some(
    (file) => file.size === 0 || file.size > 5 * 1024 * 1024,
  );
  return (
    <div className="fdcDocumentUpload">
      <label className="fdcUploadPick" htmlFor={inputId}>
        <DescriptionRounded />{" "}
        <span>{files.length ? `Replace ${label}` : `Choose ${label}`}</span>
        <small>{optional ? "Optional" : "Required"} · Max 5 MiB each</small>
      </label>
      <input
        id={inputId}
        className="fdcUploadHidden"
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(event) =>
          onChange(
            multiple
              ? Array.from(event.target.files || [])
              : event.target.files?.[0] || null,
          )
        }
      />
      {files.map((file, i) => {
        const image = file.type.startsWith("image/");
        const pdf = file.type === "application/pdf";
        return (
          <div className="fdcUploadItem" key={`${file.name}-${i}`}>
            {image && (
              <button
                type="button"
                className="fdcThumbButton"
                onClick={() => setPreview(previews[i])}
              >
                <img src={previews[i]?.url} alt={`Preview ${file.name}`} />
              </button>
            )}
            {!image && <DescriptionRounded />}
            <div className="fdcUploadDetails">
              <strong>{file.name}</strong>
              <span className={invalid ? "fdcFileError" : "fdcFileSuccess"}>
                {invalid
                  ? "Invalid file — select a non-empty file under 5 MiB"
                  : "✓ File selected and ready to submit"}
              </span>
              <small>{(file.size / 1024).toFixed(1)} KB</small>
            </div>
            {(image || pdf) && (
              <button
                type="button"
                className="fdcFileAction"
                onClick={() => setPreview(previews[i])}
              >
                Preview
              </button>
            )}
            <button
              type="button"
              className="fdcFileAction"
              onClick={() =>
                onChange(multiple ? files.filter((_, j) => i !== j) : null)
              }
              aria-label={`Remove ${file.name}`}
            >
              Remove
            </button>
          </div>
        );
      })}
      {preview && (
        <div
          className="fdcPreviewOverlay"
          role="presentation"
          onClick={() => setPreview(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Document preview"
            className="fdcPreviewModal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreview(null)}
              aria-label="Close preview"
            >
              ✕ Close
            </button>
            {preview.file.type.startsWith("image/") ? (
              <img src={preview.url} alt={preview.file.name} />
            ) : (
              <iframe src={preview.url} title={preview.file.name} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const initial = {
  firstName: "",
  middleName: "",
  surname: "",
  gender: "",
  dateOfBirth: "",
  maritalStatus: "",
  nationality: "Nigeria",
  phone: "",
  alternatePhone: "",
  email: "",
  nin: "",

  stateOfOrigin: "",
  lgaOfOrigin: "",
  hometown: "",
  originRegion: "",

  residentialCountry: "Nigeria",
  residentialState: "",
  city: "",
  localGovernment: "",
  area: "",
  street: "",
  postalCode: "",

  emergencyName: "",
  emergencyRelationship: "",
  emergencyGender: "",
  emergencyPhone: "",
  emergencyAlternatePhone: "",
  emergencyEmail: "",
  emergencyCountryOfOrigin: "Nigeria",
  emergencyStateOfOrigin: "",
  emergencyLgaOfOrigin: "",
  emergencyHometown: "",
  emergencyResidentialCountry: "Nigeria",
  emergencyResidentialState: "",
  emergencyResidentialLga: "",
  emergencyCity: "",
  emergencyArea: "",
  emergencyStreet: "",
  emergencyPostalCode: "",

  nextOfKinName: "",
  nextOfKinRelationship: "",
  nextOfKinGender: "",
  nextOfKinPhone: "",
  nextOfKinAlternatePhone: "",
  nextOfKinEmail: "",
  nextOfKinCountryOfOrigin: "Nigeria",
  nextOfKinStateOfOrigin: "",
  nextOfKinLgaOfOrigin: "",
  nextOfKinHometown: "",
  nextOfKinResidentialCountry: "Nigeria",
  nextOfKinResidentialState: "",
  nextOfKinResidentialLga: "",
  nextOfKinCity: "",
  nextOfKinArea: "",
  nextOfKinStreet: "",
  nextOfKinPostalCode: "",

  education: [emptyEducation()],

  bankName: "",
  accountName: "",
  accountNumber: "",
  bankCode: "",

  passportPhoto: null,
  cvFile: null,
  idType: "",
  idNumber: "",
  idDocument: null,
  additionalDocuments: [],

  assignedState: "",
  employmentType: "FULL_TIME",
  joinedAt: new Date().toISOString().slice(0, 10),
  rankName: "Field Development Consultant",
  rankLevel: "LEVEL-01",
  grade: "",

  termsAccepted: false,
};

const fileInfo = (file) =>
  file
    ? {
        name: file.name,
        type: file.type || "application/octet-stream",
        size: file.size,
        lastModified: file.lastModified,
      }
    : null;

export default function CareerFDCApplication() {
  const navigate = useNavigate();
  // The guide is available on every visit; formal acceptance is collected separately.
  const activeStaff = {};
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useReducer((a, b) => ({ ...a, ...b }), initial);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [applicationId] = useState(
    () =>
      `FDC-${window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`}`,
  );

  const states = useMemo(() => NIGERIAN_STATES, []);
  const activeStep = steps.find((item) => item.id === step) || steps[0];
  const completion = Math.round((step / steps.length) * 100);

  const schemas = {
    1: Yup.object({
      firstName: Yup.string().required("First name is required"),
      surname: Yup.string().required("Surname is required"),
      gender: Yup.string().required("Gender is required"),
      dateOfBirth: Yup.string().required("Date of birth is required"),
      phone: Yup.string().required("Phone is required"),
      email: Yup.string()
        .email("Enter a valid email")
        .required("Email is required"),
      nin: Yup.string().optional(),
      stateOfOrigin: Yup.string().required("State of origin is required"),
      lgaOfOrigin: Yup.string().required("LGA of origin is required"),
      residentialState: Yup.string().required("Residential state is required"),
      city: Yup.string().required("City is required"),
    }),
    2: Yup.object({
      emergencyName: Yup.string().required(
        "Emergency contact name is required",
      ),
      emergencyRelationship: Yup.string().required("Relationship is required"),
      emergencyPhone: Yup.string().required(
        "Emergency contact phone is required",
      ),
      emergencyStateOfOrigin: Yup.string().required(
        "Emergency contact state of origin is required",
      ),
      emergencyResidentialState: Yup.string().required(
        "Emergency contact residential state is required",
      ),
      nextOfKinName: Yup.string().required("Next of kin name is required"),
      nextOfKinRelationship: Yup.string().required(
        "Next of kin relationship is required",
      ),
      nextOfKinPhone: Yup.string().required("Next of kin phone is required"),
      nextOfKinStateOfOrigin: Yup.string().required(
        "Next of kin state of origin is required",
      ),
    }),
    4: Yup.object({}),
    5: Yup.object({
      assignedState: Yup.string().required("Select your preferred work state"),
    }),
  };

  const validate = async () => {
    try {
      if (schemas[step])
        await schemas[step].validate(data, { abortEarly: false });
      if (step === 3 && !data.education.some((x) => x.level && x.institution)) {
        setErrors({ education: "Add at least one education record." });
        return false;
      }
      if (step === 4 && (!data.passportPhoto || !data.cvFile)) {
        setErrors({
          documents: "Passport photograph and CV are required.",
        });
        return false;
      }
      if (step === 4) {
        const files = [
          data.passportPhoto,
          data.cvFile,
          ...(data.idDocument ? [data.idDocument] : []),
          ...data.additionalDocuments,
        ];
        if (
          data.additionalDocuments.length > 3 ||
          files.some(
            (file) => !file || file.size === 0 || file.size > 5 * 1024 * 1024,
          )
        ) {
          setErrors({
            documents:
              "Choose up to three additional documents. Each file must be non-empty and 5 MiB or smaller.",
          });
          return false;
        }
      }
      setErrors({});
      return true;
    } catch (e) {
      const nextErrors = {};
      e.inner?.forEach((v) => {
        nextErrors[v.path] = v.message;
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const next = async () => {
    if (await validate()) setStep((v) => Math.min(7, v + 1));
  };

  const updateEducation = (index, patch) => {
    const education = data.education.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    setData({ education });
  };

  const addEducation = () =>
    setData({ education: [...data.education, emptyEducation()] });
  const removeEducation = (index) => {
    if (data.education.length === 1) return;
    setData({ education: data.education.filter((_, i) => i !== index) });
  };

  const payload = () => ({
    applicationId,
    termsAccepted: data.termsAccepted,
    staffData: {
      staffId: "",

      personalInfo: {
        firstName: data.firstName.trim(),
        middleName: data.middleName.trim(),
        surname: data.surname.trim(),
        gender: data.gender,
        dateOfBirth: data.dateOfBirth || null,
        maritalStatus: data.maritalStatus || "",
        nationality: data.nationality,
        phone: data.phone.trim(),
        alternatePhone: data.alternatePhone.trim(),
        email: data.email.trim().toLowerCase(),
        photo: "",
        nationalIdentification: {
          nin: data.nin.trim(),
        },
        origin: {
          country: data.nationality || "Nigeria",
          state: data.stateOfOrigin,
          localGovernment: data.lgaOfOrigin,
          hometown: data.hometown,
          region: data.originRegion,
        },
        address: {
          country: data.residentialCountry,
          state: data.residentialState,
          city: data.city,
          localGovernment: data.localGovernment,
          area: data.area,
          street: data.street,
          postalCode: data.postalCode,
        },
        emergencyContact: {
          name: data.emergencyName,
          relationship: data.emergencyRelationship,
          gender: data.emergencyGender,
          phone: data.emergencyPhone,
          alternatePhone: data.emergencyAlternatePhone,
          email: data.emergencyEmail.trim().toLowerCase(),
          origin: {
            country: data.emergencyCountryOfOrigin,
            state: data.emergencyStateOfOrigin,
            localGovernment: data.emergencyLgaOfOrigin,
            hometown: data.emergencyHometown,
          },
          address: {
            country: data.emergencyResidentialCountry,
            state: data.emergencyResidentialState,
            localGovernment: data.emergencyResidentialLga,
            city: data.emergencyCity,
            area: data.emergencyArea,
            street: data.emergencyStreet,
            postalCode: data.emergencyPostalCode,
          },
        },
        nextOfKin: {
          name: data.nextOfKinName,
          relationship: data.nextOfKinRelationship,
          gender: data.nextOfKinGender,
          phone: data.nextOfKinPhone,
          alternatePhone: data.nextOfKinAlternatePhone,
          email: data.nextOfKinEmail.trim().toLowerCase(),
          origin: {
            country: data.nextOfKinCountryOfOrigin,
            state: data.nextOfKinStateOfOrigin,
            localGovernment: data.nextOfKinLgaOfOrigin,
            hometown: data.nextOfKinHometown,
          },
          address: {
            country: data.nextOfKinResidentialCountry,
            state: data.nextOfKinResidentialState,
            localGovernment: data.nextOfKinResidentialLga,
            city: data.nextOfKinCity,
            area: data.nextOfKinArea,
            street: data.nextOfKinStreet,
            postalCode: data.nextOfKinPostalCode,
          },
        },
      },
      education: {
        history: data.education
          .filter((x) => x.level || x.institution)
          .map((x) => ({ ...x })),
      },
      // Banking details will be collected during a later onboarding phase.
      documents: {
        passport: {
          ...fileInfo(data.passportPhoto),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        },
        cv: {
          ...fileInfo(data.cvFile),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        },
        identification: {
          ...(data.idType ? { idType: data.idType } : {}),
          ...(data.idNumber.trim() ? { number: data.idNumber.trim() } : {}),
          ...(data.idDocument ? fileInfo(data.idDocument) : {}),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        },
        profileDocuments: data.additionalDocuments.map((file) => ({
          category: "ADDITIONAL_DOCUMENT",
          ...fileInfo(file),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        })),
        employmentDocuments: [],
      },
      role: {
        roleId: "ROLE-FDC",
        name: "FDC",
        title: "Field Development Consultant",
        level: "FIELD",
        permissions: [],
      },

      createdAt: new Date(),
      updatedAt: new Date(),
    },
    organization: {
      regionId:
        activeStaff?.organization?.regionId ||
        activeStaff?.territory?.regionId ||
        null,
      stateId: null,
      reportsTo: { staffId: null, role: "FDC" },
      jurisdiction: {
        regionId: activeStaff?.organization?.regionId || null,
        stateIds: [],
        stateNames: [data.assignedState],
      },
    },
    employment: {
      status: "PENDING_APPROVAL",
      employmentType: data.employmentType,
      joinedAt: data.joinedAt,
      confirmedAt: null,
      terminatedAt: null,
      terminationReason: null,
      rank: { name: data.rankName, level: data.rankLevel, grade: data.grade },
      allowances: { enabled: true, items: [] },

      territory: {
        regionId: activeStaff?.territory?.regionId || null,
        regionName: activeStaff?.territory?.regionName || "",
        states: [
          {
            stateId: null,
            stateName: data.assignedState,
            status: "PENDING_APPROVAL",
            assignedAt: new Date(),
          },
        ],
        totalStates: 1,
        assignedAt: new Date(),
        lastUpdatedAt: new Date(),
      },
      management: { clients: { total: 0, active: 0, inactive: 0, pending: 0 } },
      performance: {
        currentPeriod: {
          target: 0,
          achieved: 0,
          percentage: 0,
          clientsRegistered: 0,
          activeClients: 0,
        },
        lifetime: {
          clientsRegistered: 0,
          targetsAssigned: 0,
          targetsCompleted: 0,
        },
        lastCalculatedAt: null,
      },
      targets: {
        currentTargetId: null,
        assigned: 0,
        completed: 0,
        pending: 0,
        lastAssignedAt: null,
      },

      audit: {
        createdBy: "CAREER_PORTAL",
        lastModifiedBy: "",
        approvedBy: null,
        approvedAt: null,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    onboarding: {
      submittedBy: {
        staffId: null,
        role: "APPLICANT",
        name: [data.firstName, data.surname].filter(Boolean).join(" "),
      },
      regionId: null,
      state: data.assignedState,
      approvalStatus: "PENDING_APPROVAL",
    },
  });

  const submit = async () => {
    if (loading) return;
    if (!data.termsAccepted) {
      return enqueueSnackbar("Confirm the information before submitting.", {
        variant: "warning",
      });
    }
    const files = [
      data.passportPhoto,
      data.cvFile,
      ...(data.idDocument ? [data.idDocument] : []),
      ...data.additionalDocuments,
    ];
    if (
      !data.passportPhoto ||
      !data.cvFile ||
      data.additionalDocuments.length > 3
    ) {
      setStep(4);
      return enqueueSnackbar(
        "Provide the required documents (up to three additional files).",
        { variant: "warning" },
      );
    }
    if (files.some((file) => file.size === 0 || file.size > 5 * 1024 * 1024)) {
      setStep(4);
      return enqueueSnackbar(
        "Each document must be no larger than 5 MiB and cannot be empty.",
        { variant: "warning" },
      );
    }
    try {
      setLoading(true);
      for (const schema of Object.values(schemas))
        await schema.validate(data, { abortEarly: false });
      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload()));
      formData.append("passportPhoto", data.passportPhoto);
      formData.append("cvFile", data.cvFile);
      if (data.idDocument) formData.append("idDocument", data.idDocument);
      data.additionalDocuments.forEach((file) =>
        formData.append("additionalDocuments", file),
      );
      const r = await submitFDCCareerApplication(formData);
      if (r?.status !== 201 || r.applicationId !== applicationId) {
        throw new Error(
          r?.message || "The application was not confirmed. Please retry.",
        );
      }
      enqueueSnackbar(r?.message || "FDC application submitted for review.", {
        variant: "success",
      });
      navigate("/career/field-development-consultant/application/success", {
        replace: true,
        state: {
          submission: {
            status: r.status,
            applicationId: r.applicationId,
            applicationStatus: r.applicationStatus,
          },
          applicantName: [data.firstName, data.surname]
            .filter(Boolean)
            .join(" "),
        },
      });
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          e.message ||
          "Unable to submit FDC application.",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fdcCareerAppPage fdcCareerAppOnboarding">
      <div style={{ maxWidth: 1100, padding: "10px 16px", fontSize: "1.5rem" }}>
        <a
          href="/career/field-development-consultant/requirements"
          style={{ color: "#145ba0", fontWeight: 700 }}
        >
          ← Review eligibility and application guide
        </a>
      </div>
      <div className="fdcCareerAppOnboardingShell">
        <header className="fdcCareerAppSaasHeader">
          <div className="fdcCareerAppSaasHeaderMain">
            <div className="fdcCareerAppSaasMark" aria-hidden="true">
              <BadgeRounded />
            </div>

            <div className="fdcCareerAppSaasTitle">
              <div className="fdcCareerAppSaasEyebrow">
                <span>UNIVERSE WORKFORCE</span>
                <i />
                <span>CAREER ONBOARDING</span>
              </div>
              <h1>Field Development Consultant application</h1>
              <p>
                Build a complete FDC personnel record through a controlled,
                review-ready onboarding workflow for{" "}
                {activeStaff?.territory?.regionName || "your assigned region"}.
              </p>
            </div>

            <div className="fdcCareerAppWorkflowBadge">
              <ShieldRounded />
              <span>
                <small>ACCOUNT STATE</small>
                <strong>Pending approval</strong>
              </span>
            </div>
          </div>

          <div className="fdcCareerAppHeaderMetrics">
            <article>
              <span className="fdcCareerAppMetricIcon">
                <FactCheckRounded />
              </span>
              <div>
                <small>WORKFLOW</small>
                <strong>7-stage personnel intake</strong>
              </div>
            </article>
            <article>
              <span className="fdcCareerAppMetricIcon">
                <activeStep.icon />
              </span>
              <div>
                <small>CURRENT STAGE</small>
                <strong>{activeStep.title}</strong>
              </div>
            </article>
            <article>
              <span className="fdcCareerAppMetricIcon">
                <VerifiedUserRounded />
              </span>
              <div>
                <small>ROLE</small>
                <strong>Field Development Consultant</strong>
              </div>
            </article>
          </div>
        </header>

        <section className="fdcCareerAppProgressPanel" aria-label="Application progress">
          <div className="fdcCareerAppProgressMeta">
            <div>
              <span>
                Step {step} of {steps.length}
              </span>
              <strong>{activeStep.title}</strong>
            </div>
            <b>{completion}%</b>
          </div>

          <div className="fdcCareerAppProgressTrack" aria-hidden="true">
            <span style={{ width: `${completion}%` }} />
          </div>

          <nav className="fdcCareerAppOnboardSteps" aria-label="FDC onboarding steps">
            {steps.map((s) => {
              const isDone = step > s.id;
              const isActive = step === s.id;
              return (
                <button
                  type="button"
                  key={s.id}
                  className={`${isActive ? "active" : ""} ${isDone ? "done" : ""}`}
                  onClick={() => s.id < step && setStep(s.id)}
                  disabled={s.id > step}
                  aria-current={isActive ? "step" : undefined}
                >
                  <span className="fdcCareerAppStepIcon">
                    {isDone ? <CheckRounded /> : <s.icon />}
                  </span>
                  <span className="fdcCareerAppStepText">
                    <small>{String(s.id).padStart(2, "0")}</small>
                    <strong>{s.title}</strong>
                  </span>
                </button>
              );
            })}
          </nav>
        </section>

        <div className="fdcCareerAppWorkspace" style={{marginTop: "2.7rem"}}>
          <main className="fdcCareerAppOnboardForm">
            <div className="fdcCareerAppFormStageHeader">
              <div>
                <span>APPLICATION SECTION</span>
                <h2>{activeStep.title}</h2>
              </div>
              <div className="fdcCareerAppStageCounter">
                <span>{step}</span>
                <small>/ {steps.length}</small>
              </div>
            </div>
            {step === 1 && (
              <>
                <Heading
                  title="Personal information"
                  text="Capture the consultant's legal identity, contact information, origin and current residential address."
                />
                <Grid>
                  <Field label="First name" error={errors.firstName}>
                    <input
                      value={data.firstName}
                      onChange={(e) => setData({ firstName: e.target.value })}
                    />
                  </Field>
                  <Field label="Middle name">
                    <input
                      value={data.middleName}
                      onChange={(e) => setData({ middleName: e.target.value })}
                    />
                  </Field>
                  <Field label="Surname" error={errors.surname}>
                    <input
                      value={data.surname}
                      onChange={(e) => setData({ surname: e.target.value })}
                    />
                  </Field>
                  <Field label="Gender" error={errors.gender}>
                    <select
                      value={data.gender}
                      onChange={(e) => setData({ gender: e.target.value })}
                    >
                      <option value="">Select</option>
                      <option>MALE</option>
                      <option>FEMALE</option>
                    </select>
                  </Field>
                  <Field label="Date of birth" error={errors.dateOfBirth}>
                    <input
                      type="date"
                      value={data.dateOfBirth}
                      onChange={(e) => setData({ dateOfBirth: e.target.value })}
                    />
                  </Field>
                  <Field label="Marital status">
                    <select
                      value={data.maritalStatus}
                      onChange={(e) =>
                        setData({ maritalStatus: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      <option>SINGLE</option>
                      <option>MARRIED</option>
                      <option>DIVORCED</option>
                      <option>WIDOWED</option>
                    </select>
                  </Field>
                  <Field label="Nationality">
                    <input
                      value={data.nationality}
                      onChange={(e) => setData({ nationality: e.target.value })}
                    />
                  </Field>
                  <Field label="NIN (optional)" error={errors.nin}>
                    <input
                      value={data.nin}
                      onChange={(e) => setData({ nin: e.target.value })}
                      placeholder="National Identification Number"
                    />
                  </Field>
                  <Field label="Phone" error={errors.phone}>
                    <input
                      value={data.phone}
                      onChange={(e) => setData({ phone: e.target.value })}
                    />
                  </Field>
                  <Field label="Alternate phone">
                    <input
                      value={data.alternatePhone}
                      onChange={(e) =>
                        setData({ alternatePhone: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <input
                      type="email"
                      value={data.email}
                      onChange={(e) => setData({ email: e.target.value })}
                    />
                  </Field>
                </Grid>

                <Heading
                  title="Origin information"
                  text="Record the consultant's state, local government and place of origin separately from the current address."
                />
                <Grid>
                  <Field label="Country of origin">
                    <input value={data.nationality} disabled />
                  </Field>
                  <Field label="State of origin" error={errors.stateOfOrigin}>
                    <StateInput
                      value={data.stateOfOrigin}
                      onChange={(value) => setData({ stateOfOrigin: value })}
                    />
                  </Field>
                  <Field label="LGA of origin" error={errors.lgaOfOrigin}>
                    <input
                      value={data.lgaOfOrigin}
                      onChange={(e) => setData({ lgaOfOrigin: e.target.value })}
                    />
                  </Field>
                  <Field label="Hometown">
                    <input
                      value={data.hometown}
                      onChange={(e) => setData({ hometown: e.target.value })}
                    />
                  </Field>
                  <Field label="Region / geopolitical zone">
                    <input
                      value={data.originRegion}
                      onChange={(e) =>
                        setData({ originRegion: e.target.value })
                      }
                      placeholder="e.g. North-West"
                    />
                  </Field>
                </Grid>

                <Heading
                  title="Current residential address"
                  text="Capture where the consultant currently lives. This remains separate from place of origin."
                />
                <Grid>
                  <Field label="Country">
                    <input
                      value={data.residentialCountry}
                      onChange={(e) =>
                        setData({ residentialCountry: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Residential state"
                    error={errors.residentialState}
                  >
                    <StateInput
                      value={data.residentialState}
                      onChange={(value) => setData({ residentialState: value })}
                    />
                  </Field>
                  <Field label="Residential city" error={errors.city}>
                    <input
                      value={data.city}
                      onChange={(e) => setData({ city: e.target.value })}
                    />
                  </Field>
                  <Field label="Residential LGA">
                    <input
                      value={data.localGovernment}
                      onChange={(e) =>
                        setData({ localGovernment: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Area">
                    <input
                      value={data.area}
                      onChange={(e) => setData({ area: e.target.value })}
                    />
                  </Field>
                  <Field label="Street">
                    <input
                      value={data.street}
                      onChange={(e) => setData({ street: e.target.value })}
                    />
                  </Field>
                  <Field label="Postal code">
                    <input
                      value={data.postalCode}
                      onChange={(e) => setData({ postalCode: e.target.value })}
                    />
                  </Field>
                </Grid>
              </>
            )}

            {step === 2 && (
              <>
                <Heading
                  title="Emergency contact"
                  text="Provide a complete profile for the person Universe Inventory should contact in an emergency."
                />
                <Grid>
                  <Field label="Full name" error={errors.emergencyName}>
                    <input
                      value={data.emergencyName}
                      onChange={(e) =>
                        setData({ emergencyName: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Relationship"
                    error={errors.emergencyRelationship}
                  >
                    <RelationshipInput
                      value={data.emergencyRelationship}
                      onChange={(value) =>
                        setData({ emergencyRelationship: value })
                      }
                    />
                  </Field>
                  <Field label="Gender">
                    <select
                      value={data.emergencyGender}
                      onChange={(e) =>
                        setData({ emergencyGender: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      <option>MALE</option>
                      <option>FEMALE</option>
                    </select>
                  </Field>
                  <Field label="Phone" error={errors.emergencyPhone}>
                    <input
                      value={data.emergencyPhone}
                      onChange={(e) =>
                        setData({ emergencyPhone: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Alternate phone">
                    <input
                      value={data.emergencyAlternatePhone}
                      onChange={(e) =>
                        setData({ emergencyAlternatePhone: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={data.emergencyEmail}
                      onChange={(e) =>
                        setData({ emergencyEmail: e.target.value })
                      }
                    />
                  </Field>
                </Grid>
                <Heading
                  title="Emergency contact origin"
                  text="Record the emergency contact's origin information."
                />
                <Grid>
                  <Field label="Country of origin">
                    <input
                      value={data.emergencyCountryOfOrigin}
                      onChange={(e) =>
                        setData({ emergencyCountryOfOrigin: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="State of origin"
                    error={errors.emergencyStateOfOrigin}
                  >
                    <StateInput
                      value={data.emergencyStateOfOrigin}
                      onChange={(value) =>
                        setData({ emergencyStateOfOrigin: value })
                      }
                    />
                  </Field>
                  <Field label="LGA of origin">
                    <input
                      value={data.emergencyLgaOfOrigin}
                      onChange={(e) =>
                        setData({ emergencyLgaOfOrigin: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Hometown">
                    <input
                      value={data.emergencyHometown}
                      onChange={(e) =>
                        setData({ emergencyHometown: e.target.value })
                      }
                    />
                  </Field>
                </Grid>
                <Heading
                  title="Emergency contact address"
                  text="Capture the emergency contact's current residential address."
                />
                <Grid>
                  <Field label="Country">
                    <input
                      value={data.emergencyResidentialCountry}
                      onChange={(e) =>
                        setData({ emergencyResidentialCountry: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="State" error={errors.emergencyResidentialState}>
                    <StateInput
                      value={data.emergencyResidentialState}
                      onChange={(value) =>
                        setData({ emergencyResidentialState: value })
                      }
                    />
                  </Field>
                  <Field label="LGA">
                    <input
                      value={data.emergencyResidentialLga}
                      onChange={(e) =>
                        setData({ emergencyResidentialLga: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="City">
                    <input
                      value={data.emergencyCity}
                      onChange={(e) =>
                        setData({ emergencyCity: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Area">
                    <input
                      value={data.emergencyArea}
                      onChange={(e) =>
                        setData({ emergencyArea: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Street / address">
                    <input
                      value={data.emergencyStreet}
                      onChange={(e) =>
                        setData({ emergencyStreet: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Postal code">
                    <input
                      value={data.emergencyPostalCode}
                      onChange={(e) =>
                        setData({ emergencyPostalCode: e.target.value })
                      }
                    />
                  </Field>
                </Grid>

                <Heading
                  title="Next of kin"
                  text="Provide the consultant's designated next-of-kin information separately from the emergency contact."
                />
                <Grid>
                  <Field label="Full name" error={errors.nextOfKinName}>
                    <input
                      value={data.nextOfKinName}
                      onChange={(e) =>
                        setData({ nextOfKinName: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="Relationship"
                    error={errors.nextOfKinRelationship}
                  >
                    <RelationshipInput
                      value={data.nextOfKinRelationship}
                      onChange={(value) =>
                        setData({ nextOfKinRelationship: value })
                      }
                    />
                  </Field>
                  <Field label="Gender">
                    <select
                      value={data.nextOfKinGender}
                      onChange={(e) =>
                        setData({ nextOfKinGender: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      <option>MALE</option>
                      <option>FEMALE</option>
                    </select>
                  </Field>
                  <Field label="Phone" error={errors.nextOfKinPhone}>
                    <input
                      value={data.nextOfKinPhone}
                      onChange={(e) =>
                        setData({ nextOfKinPhone: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Alternate phone">
                    <input
                      value={data.nextOfKinAlternatePhone}
                      onChange={(e) =>
                        setData({ nextOfKinAlternatePhone: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={data.nextOfKinEmail}
                      onChange={(e) =>
                        setData({ nextOfKinEmail: e.target.value })
                      }
                    />
                  </Field>
                </Grid>
                <Heading
                  title="Next of kin origin"
                  text="Record the next of kin's place of origin."
                />
                <Grid>
                  <Field label="Country of origin">
                    <input
                      value={data.nextOfKinCountryOfOrigin}
                      onChange={(e) =>
                        setData({ nextOfKinCountryOfOrigin: e.target.value })
                      }
                    />
                  </Field>
                  <Field
                    label="State of origin"
                    error={errors.nextOfKinStateOfOrigin}
                  >
                    <StateInput
                      value={data.nextOfKinStateOfOrigin}
                      onChange={(value) =>
                        setData({ nextOfKinStateOfOrigin: value })
                      }
                    />
                  </Field>
                  <Field label="LGA of origin">
                    <input
                      value={data.nextOfKinLgaOfOrigin}
                      onChange={(e) =>
                        setData({ nextOfKinLgaOfOrigin: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Hometown">
                    <input
                      value={data.nextOfKinHometown}
                      onChange={(e) =>
                        setData({ nextOfKinHometown: e.target.value })
                      }
                    />
                  </Field>
                </Grid>
                <Heading
                  title="Next of kin address"
                  text="Capture the next of kin's current residential address."
                />
                <Grid>
                  <Field label="Country">
                    <input
                      value={data.nextOfKinResidentialCountry}
                      onChange={(e) =>
                        setData({ nextOfKinResidentialCountry: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="State">
                    <StateInput
                      value={data.nextOfKinResidentialState}
                      onChange={(value) =>
                        setData({ nextOfKinResidentialState: value })
                      }
                    />
                  </Field>
                  <Field label="LGA">
                    <input
                      value={data.nextOfKinResidentialLga}
                      onChange={(e) =>
                        setData({ nextOfKinResidentialLga: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="City">
                    <input
                      value={data.nextOfKinCity}
                      onChange={(e) =>
                        setData({ nextOfKinCity: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Area">
                    <input
                      value={data.nextOfKinArea}
                      onChange={(e) =>
                        setData({ nextOfKinArea: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Street / address">
                    <input
                      value={data.nextOfKinStreet}
                      onChange={(e) =>
                        setData({ nextOfKinStreet: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Postal code">
                    <input
                      value={data.nextOfKinPostalCode}
                      onChange={(e) =>
                        setData({ nextOfKinPostalCode: e.target.value })
                      }
                    />
                  </Field>
                </Grid>
              </>
            )}

            {step === 3 && (
              <>
                <Heading
                  title="Educational qualifications"
                  text="Add primary, secondary, higher institution, university or other relevant qualifications."
                />
                {data.education.map((item, index) => (
                  <div className="fdcCareerAppEducationCard" key={index}>
                    <SchoolRounded />
                    <div style={{ width: "100%" }}>
                      <Grid>
                        <Field label="Education level">
                          <select
                            value={item.level}
                            onChange={(e) =>
                              updateEducation(index, { level: e.target.value })
                            }
                          >
                            <option value="">Select</option>
                            <option>PRIMARY</option>
                            <option>SECONDARY</option>
                            <option>COLLEGE</option>
                            <option>POLYTECHNIC</option>
                            <option>UNIVERSITY</option>
                            <option>POSTGRADUATE</option>
                            <option>PROFESSIONAL</option>
                            <option>OTHER</option>
                          </select>
                        </Field>
                        <Field label="Institution">
                          <input
                            value={item.institution}
                            onChange={(e) =>
                              updateEducation(index, {
                                institution: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="Qualification / certificate">
                          <input
                            value={item.qualification}
                            onChange={(e) =>
                              updateEducation(index, {
                                qualification: e.target.value,
                              })
                            }
                            placeholder="e.g. B.Sc, HND, SSCE"
                          />
                        </Field>
                        <Field label="Field of study">
                          <input
                            value={item.fieldOfStudy}
                            onChange={(e) =>
                              updateEducation(index, {
                                fieldOfStudy: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="Start year">
                          <input
                            value={item.startYear}
                            onChange={(e) =>
                              updateEducation(index, {
                                startYear: e.target.value,
                              })
                            }
                          />
                        </Field>
                        <Field label="End year">
                          <input
                            value={item.endYear}
                            onChange={(e) =>
                              updateEducation(index, {
                                endYear: e.target.value,
                              })
                            }
                          />
                        </Field>
                      </Grid>
                      {data.education.length > 1 && (
                        <button
                          className="fdcCareerAppRemoveQualification"
                          type="button"
                          onClick={() => removeEducation(index)}
                        >
                          <DeleteOutlineRounded /> Remove qualification
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {errors.education && (
                  <div className="fdcCareerAppError">{errors.education}</div>
                )}
                <button
                  className="fdcCareerAppAddQualification"
                  type="button"
                  onClick={addEducation}
                >
                  <AddRounded /> Add another qualification
                </button>
              </>
            )}

            {step === 4 && (
              <>
                {/* Banking section suspended for this application phase.
                <Heading
                  title="Salary bank account"
                  text="Collect the FDC's salary destination account. This does not set or propose a salary amount."
                />
                <Grid>
                  <Field label="Bank name" error={errors.bankName}>
                    <NigeriaBankSelect
                      value={data.bankName}
                      bankCode={data.bankCode}
                      onChange={(bank) =>
                        setData({
                          bankName: bank.name,
                          bankCode: bank.code || "",
                        })
                      }
                    />
                  </Field>
                  <Field label="Bank code">
                    <input
                      value={data.bankCode}
                      readOnly
                      placeholder="Filled automatically"
                    />
                  </Field>
                  <Field label="Account name" error={errors.accountName}>
                    <input
                      value={data.accountName}
                      onChange={(e) => setData({ accountName: e.target.value })}
                    />
                  </Field>
                  <Field label="Account number" error={errors.accountNumber}>
                    <input
                      inputMode="numeric"
                      value={data.accountNumber}
                      onChange={(e) =>
                        setData({
                          accountNumber: e.target.value.replace(/\D/g, ""),
                        })
                      }
                    />
                  </Field>
                </Grid>
                */}
                <Heading
                  title="Documents"
                  text="Attach the personnel documents required for review and verification."
                />
                <Grid>
                  <Field label="Passport photograph (required)">
                    <DocumentUpload
                      label="passport photograph"
                      value={data.passportPhoto}
                      onChange={(passportPhoto) => setData({ passportPhoto })}
                      accept=".jpg,.jpeg,.png,.webp"
                    />
                  </Field>
                  <Field label="Curriculum Vitae (required)">
                    <DocumentUpload
                      label="CV"
                      value={data.cvFile}
                      onChange={(cvFile) => setData({ cvFile })}
                      accept=".pdf,.doc,.docx"
                    />
                  </Field>
                  <Field label="ID type (optional)">
                    <select
                      value={data.idType}
                      onChange={(event) =>
                        setData({ idType: event.target.value })
                      }
                    >
                      <option value="">Not provided</option>
                      <option>NIN_SLIP</option>
                      <option>NATIONAL_ID_CARD</option>
                      <option>VOTERS_CARD</option>
                      <option>DRIVERS_LICENSE</option>
                      <option>INTERNATIONAL_PASSPORT</option>
                    </select>
                  </Field>
                  <Field label="ID number (optional)">
                    <input
                      value={data.idNumber}
                      onChange={(event) =>
                        setData({ idNumber: event.target.value })
                      }
                    />
                  </Field>
                  <Field label="Identification document (optional)">
                    <DocumentUpload
                      label="identification document"
                      value={data.idDocument}
                      onChange={(idDocument) => setData({ idDocument })}
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      optional
                    />
                  </Field>
                  <Field label="Additional supporting documents (optional)">
                    <DocumentUpload
                      label="supporting documents"
                      value={data.additionalDocuments}
                      onChange={(additionalDocuments) => {
                        if (additionalDocuments.length > 3) {
                          enqueueSnackbar(
                            "Select no more than three additional documents.",
                            { variant: "warning" },
                          );
                          return;
                        }
                        setData({ additionalDocuments });
                      }}
                      accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                      multiple
                      optional
                    />
                  </Field>
                </Grid>
                {errors.documents && (
                  <div className="fdcCareerAppError">{errors.documents}</div>
                )}
                <div className="fdcCareerAppSecurityNotice">
                  <DescriptionRounded />
                  <div>
                    <strong>Document verification</strong>
                    <span>
                      Uploaded documents are retained for recruitment review and
                      remain unverified until an authorized reviewer checks
                      them.
                    </span>
                  </div>
                </div>
              </>
            )}

            {step === 5 && (
              <>
                <Heading
                  title="Your preferred work location"
                  text="Choose the primary Nigerian state where you would like to work as a Field Development Consultant. This is your preference, not a confirmed posting."
                />
                <div className="fdcCareerAppWorkLocationIntro">
                  <LocationOnRounded />
                  <div>
                    <strong>Where would you prefer to work?</strong>
                    <p>Select one state. Recruitment will review this preference when considering your application.</p>
                  </div>
                </div>
                <Grid>
                  <Field label="Preferred state of work" error={errors.assignedState}>
                    <select
                      value={data.assignedState}
                      onChange={(e) => setData({ assignedState: e.target.value })}
                    >
                      <option value="">Select your preferred state</option>
                      {states.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </Field>
                </Grid>
                {data.assignedState && (
                  <div className="fdcCareerAppWorkLocationSelected">
                    <CheckCircleRounded />
                    <span>Your preference: <strong>{data.assignedState}</strong></span>
                  </div>
                )}
              </>
            )}

            {step === 6 && (
              <>
                <Heading
                  title="Review your application"
                  text="Please check each section carefully. Use the Edit buttons to return to any section before you submit."
                />
                <FDCApplicationReview data={data} applicationId={applicationId} onEdit={setStep} />
                <label className="fdcCareerAppConfirm fdcCareerAppReviewConsent">
                  <input
                    type="checkbox"
                    checked={data.termsAccepted}
                    onChange={(e) => setData({ termsAccepted: e.target.checked })}
                  />
                  <span>
                    I confirm that the information above is accurate, I have reviewed the{" "}
                    <a href="/career/field-development-consultant/fdc-policy" target="_blank" rel="noopener noreferrer">
                      FDC Terms of Service
                    </a>, and I understand my application is subject to recruitment review and approval.
                  </span>
                </label>
              </>
            )}

            <footer className="fdcCareerAppOnboardFooter">
              <button
                type="button"
                disabled={step === 1 || loading}
                onClick={() => setStep((v) => v - 1)}
              >
                <ArrowBackRounded /> Back
              </button>
              {step < 6 ? (
                <button type="button" className="primary" onClick={next}>
                  Continue <ArrowForwardRounded />
                </button>
              ) : (
                <button
                  type="button"
                  className="primary"
                  disabled={loading}
                  onClick={submit}
                >
                  <PersonAddAltRounded />
                  {loading ? "Submitting..." : "Submit FDC for approval"}
                </button>
              )}
            </footer>
          </main>

          <aside
            className="fdcCareerAppContextPanel"
            aria-label="Application overview"
            style={{ maxWidth: "47rem" }}
          >
            <section className="fdcCareerAppCareerMotionCard">
              <div className="fdcCareerAppCareerMotionTop">
                <span className="fdcCareerAppCareerMotionIcon">
                  <WorkRounded />
                </span>
                <div>
                  <small>FDC CAREER APPLICATION</small>
                  <strong>Building your professional profile</strong>
                </div>
              </div>

              <div className="fdcCareerAppCareerVisual" aria-hidden="true">
                <span className="fdcCareerAppCareerGlow fdcCareerAppCareerGlow--one" />
                <span className="fdcCareerAppCareerGlow fdcCareerAppCareerGlow--two" />
                <span className="fdcCareerAppCareerOrbit fdcCareerAppCareerOrbit--outer">
                  <i className="fdcCareerAppCareerNode fdcCareerAppCareerNode--one" />
                  <i className="fdcCareerAppCareerNode fdcCareerAppCareerNode--two" />
                  <i className="fdcCareerAppCareerNode fdcCareerAppCareerNode--three" />
                </span>
                <span className="fdcCareerAppCareerOrbit fdcCareerAppCareerOrbit--inner" />
                <span className="fdcCareerAppCareerCore">
                  <WorkRounded />
                </span>
              </div>

              <div className="fdcCareerAppCareerStatus">
                <span className="fdcCareerAppCareerPulse" />
                <div>
                  <strong>
                    Section {step} of {steps.length}
                  </strong>
                  <p>
                    You’re currently completing <b>{activeStep.title}</b>. Your
                    entered information stays available as you move between
                    completed sections.
                  </p>
                </div>
              </div>
            </section>

            <section className="fdcCareerAppContextCard fdcCareerAppContextCard--secure">
              <div className="fdcCareerAppContextSecurityIcon">
                <ShieldRounded />
              </div>
              <small>CONTROLLED ONBOARDING</small>
              <strong>Approval-gated access</strong>
              <p>
                Submitting this application does not activate the FDC account.
                Authentication remains locked until authorized approval.
              </p>
            </section>

            <section className="fdcCareerAppContextCard fdcCareerAppContextCard--help">
              <InfoOutlined />
              <div>
                <strong>Safe to review and correct</strong>
                <p>
                  Use the completed stages in the top navigation to revisit
                  information without losing what has already been entered.
                </p>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

const NIGERIAN_BANKS_API =
  "https://cdn.jsdelivr.net/gh/supermx1/nigerian-banks-api@main/data.json";
const NIGERIAN_BANK_LOGO_BASE =
  "https://cdn.jsdelivr.net/gh/supermx1/nigerian-banks-api@main/logos";

function NigeriaBankSelect({ value, bankCode, onChange }) {
  const [banks, setBanks] = React.useState([]);
  const [query, setQuery] = React.useState(value || "");
  const [open, setOpen] = React.useState(false);
  const [loadingBanks, setLoadingBanks] = React.useState(false);
  const [bankLoadError, setBankLoadError] = React.useState(false);

  React.useEffect(() => {
    setQuery(value || "");
  }, [value]);

  React.useEffect(() => {
    let alive = true;
    const loadBanks = async () => {
      try {
        setLoadingBanks(true);
        setBankLoadError(false);
        const response = await fetch(NIGERIAN_BANKS_API);
        if (!response.ok) throw new Error("Unable to load Nigerian banks");
        const raw = await response.json();
        const list = Array.isArray(raw) ? raw : raw?.banks || raw?.data || [];
        const normalized = list
          .map((bank) => {
            const name =
              bank?.name ||
              bank?.Name ||
              bank?.bank_name ||
              bank?.bankName ||
              "";
            const code = String(
              bank?.code ||
                bank?.Code ||
                bank?.bank_code ||
                bank?.bankCode ||
                "",
            );
            const slug =
              bank?.slug ||
              bank?.Slug ||
              name
                .toLowerCase()
                .replace(/&/g, "and")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
            const logo =
              bank?.logo ||
              bank?.logo_url ||
              bank?.logoUrl ||
              (slug ? `${NIGERIAN_BANK_LOGO_BASE}/${slug}.png` : "");
            return { name, code, slug, logo };
          })
          .filter((bank) => bank.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (alive) setBanks(normalized);
      } catch (error) {
        if (alive) setBankLoadError(true);
      } finally {
        if (alive) setLoadingBanks(false);
      }
    };
    loadBanks();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = banks
    .filter((bank) => bank.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 80);

  const selected = banks.find(
    (bank) => bank.name === value || (bankCode && bank.code === bankCode),
  );

  return (
    <div className="fdcCareerAppBankSelect">
      <button
        type="button"
        className="fdcCareerAppBankSelectControl"
        onClick={() => setOpen((current) => !current)}
      >
        <BankLogo bank={selected} />
        <span>
          <strong>{value || "Select Nigerian bank"}</strong>
          <small>
            {bankCode
              ? `Bank code ${bankCode}`
              : "Search the Nigerian bank directory"}
          </small>
        </span>
        <span className="fdcCareerAppBankSelectChevron">⌄</span>
      </button>

      {open && (
        <div className="fdcCareerAppBankSelectMenu">
          <div className="fdcCareerAppBankSearch">
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search bank..."
            />
          </div>

          <div className="fdcCareerAppBankOptions">
            {loadingBanks && (
              <div className="fdcCareerAppBankStatus">Loading Nigerian banks…</div>
            )}
            {bankLoadError && (
              <div className="fdcCareerAppBankStatus error">
                Bank directory could not load. Check your internet connection
                and try again.
              </div>
            )}
            {!loadingBanks && !bankLoadError && filtered.length === 0 && (
              <div className="fdcCareerAppBankStatus">No matching bank found.</div>
            )}
            {!loadingBanks &&
              filtered.map((bank) => (
                <button
                  type="button"
                  key={`${bank.code}-${bank.name}`}
                  onClick={() => {
                    onChange(bank);
                    setQuery(bank.name);
                    setOpen(false);
                  }}
                >
                  <BankLogo bank={bank} />
                  <span>
                    <strong>{bank.name}</strong>
                    <small>{bank.code ? `Code ${bank.code}` : "Nigeria"}</small>
                  </span>
                  {bank.name === value && <CheckCircleRounded />}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BankLogo({ bank }) {
  const [failed, setFailed] = React.useState(false);
  const initials = (bank?.name || "Bank")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  if (!bank?.logo || failed) {
    return <span className="fdcCareerAppBankLogoFallback">{initials}</span>;
  }

  return (
    <span className="fdcCareerAppBankLogo">
      <img src={bank.logo} alt="" onError={() => setFailed(true)} />
    </span>
  );
}

const reviewText = (value) =>
  value === null || value === undefined || String(value).trim() === "" ? "Not provided" : String(value);

function ReviewDetail({ label, value }) {
  return (
    <div className="fdcCareerAppReviewDetail">
      <dt>{label}</dt>
      <dd>{reviewText(value)}</dd>
    </div>
  );
}

function ReviewSection({ icon: Icon, title, subtitle, step, onEdit, children }) {
  return (
    <section className="fdcCareerAppReviewSection" aria-label={title}>
      <header className="fdcCareerAppReviewSectionHead">
        <span className="fdcCareerAppReviewSectionIcon"><Icon /></span>
        <div className="fdcCareerAppReviewSectionTitle">
          <h4>{title}</h4>
          <p>{subtitle}</p>
        </div>
        <button type="button" className="fdcCareerAppReviewEdit" onClick={() => onEdit(step)} aria-label={`Edit ${title}`}>
          Edit <ArrowForwardRounded fontSize="small" />
        </button>
      </header>
      <div className="fdcCareerAppReviewSectionBody">{children}</div>
    </section>
  );
}

function ReviewDetails({ items }) {
  return <dl className="fdcCareerAppReviewDetails">{items.map(([label, value]) => <ReviewDetail key={label} label={label} value={value} />)}</dl>;
}

function ReviewFile({ label, file }) {
  return (
    <div className="fdcCareerAppReviewFile">
      <DescriptionRounded />
      <span><strong>{label}</strong><small>{file ? `${file.name} · ${(file.size / 1024).toFixed(1)} KB` : "Not provided"}</small></span>
      <span className={file ? "fdcCareerAppReviewFileReady" : "fdcCareerAppReviewFileOptional"}>{file ? "Selected" : "Optional"}</span>
    </div>
  );
}

function FDCApplicationReview({ data, applicationId, onEdit }) {
  const fullName = [data.firstName, data.middleName, data.surname].filter(Boolean).join(" ");
  const qualifications = data.education.filter(item => item.level || item.institution || item.qualification);
  return (
    <div className="fdcCareerAppReviewExperience">
      <div className="fdcCareerAppReviewHero">
        <span className="fdcCareerAppReviewHeroIcon"><FactCheckRounded /></span>
        <div>
          <span className="fdcCareerAppReviewEyebrow">FINAL APPLICATION CHECK</span>
          <h3>{fullName || "Your FDC application"}</h3>
          <p>Review all details before submitting your application for consideration.</p>
          <div className="fdcCareerAppReviewHeroMeta"><span>Reference: {applicationId}</span><span>7-stage application</span></div>
        </div>
      </div>

      <ReviewSection icon={PersonRounded} title="Personal and contact details" subtitle="Identity and ways to reach you" step={1} onEdit={onEdit}>
        <ReviewDetails items={[
          ["First name", data.firstName], ["Middle name", data.middleName], ["Surname", data.surname],
          ["Gender", data.gender], ["Date of birth", data.dateOfBirth], ["Marital status", data.maritalStatus],
          ["Nationality", data.nationality], ["NIN (optional)", data.nin], ["Phone", data.phone],
          ["Alternate phone", data.alternatePhone], ["Email address", data.email],
        ]} />
        <h5 className="fdcCareerAppReviewSubheading">Place of origin</h5>
        <ReviewDetails items={[["Country", data.nationality], ["State", data.stateOfOrigin], ["LGA", data.lgaOfOrigin], ["Hometown", data.hometown], ["Geopolitical zone", data.originRegion]]} />
        <h5 className="fdcCareerAppReviewSubheading">Residential address</h5>
        <ReviewDetails items={[["Country", data.residentialCountry], ["State", data.residentialState], ["City", data.city], ["LGA", data.localGovernment], ["Area", data.area], ["Street", data.street], ["Postal code", data.postalCode]]} />
      </ReviewSection>

      <ReviewSection icon={LocationOnRounded} title="Emergency contact" subtitle="Person to contact in an emergency" step={2} onEdit={onEdit}>
        <ReviewDetails items={[["Full name", data.emergencyName], ["Relationship", data.emergencyRelationship], ["Gender", data.emergencyGender], ["Phone", data.emergencyPhone], ["Alternate phone", data.emergencyAlternatePhone], ["Email", data.emergencyEmail]]} />
        <h5 className="fdcCareerAppReviewSubheading">Origin</h5>
        <ReviewDetails items={[["Country", data.emergencyCountryOfOrigin], ["State", data.emergencyStateOfOrigin], ["LGA", data.emergencyLgaOfOrigin], ["Hometown", data.emergencyHometown]]} />
        <h5 className="fdcCareerAppReviewSubheading">Residential address</h5>
        <ReviewDetails items={[["Country", data.emergencyResidentialCountry], ["State", data.emergencyResidentialState], ["LGA", data.emergencyResidentialLga], ["City", data.emergencyCity], ["Area", data.emergencyArea], ["Street", data.emergencyStreet], ["Postal code", data.emergencyPostalCode]]} />
      </ReviewSection>

      <ReviewSection icon={VerifiedUserRounded} title="Next of kin" subtitle="Designated next-of-kin information" step={2} onEdit={onEdit}>
        <ReviewDetails items={[["Full name", data.nextOfKinName], ["Relationship", data.nextOfKinRelationship], ["Gender", data.nextOfKinGender], ["Phone", data.nextOfKinPhone], ["Alternate phone", data.nextOfKinAlternatePhone], ["Email", data.nextOfKinEmail]]} />
        <h5 className="fdcCareerAppReviewSubheading">Origin</h5>
        <ReviewDetails items={[["Country", data.nextOfKinCountryOfOrigin], ["State", data.nextOfKinStateOfOrigin], ["LGA", data.nextOfKinLgaOfOrigin], ["Hometown", data.nextOfKinHometown]]} />
        <h5 className="fdcCareerAppReviewSubheading">Residential address</h5>
        <ReviewDetails items={[["Country", data.nextOfKinResidentialCountry], ["State", data.nextOfKinResidentialState], ["LGA", data.nextOfKinResidentialLga], ["City", data.nextOfKinCity], ["Area", data.nextOfKinArea], ["Street", data.nextOfKinStreet], ["Postal code", data.nextOfKinPostalCode]]} />
      </ReviewSection>

      <ReviewSection icon={SchoolRounded} title="Education and qualifications" subtitle={`${qualifications.length} qualification(s) provided`} step={3} onEdit={onEdit}>
        {qualifications.length ? qualifications.map((item, index) => (
          <div className="fdcCareerAppReviewEducation" key={index}>
            <strong>Qualification {index + 1}</strong>
            <ReviewDetails items={[["Education level", item.level], ["Institution", item.institution], ["Certificate", item.qualification], ["Field of study", item.fieldOfStudy], ["Start year", item.startYear], ["End year", item.endYear]]} />
          </div>
        )) : <p>No qualifications recorded.</p>}
      </ReviewSection>

      <ReviewSection icon={DescriptionRounded} title="Documents" subtitle="Selected files, not yet uploaded" step={4} onEdit={onEdit}>
        <div className="fdcCareerAppReviewFiles">
          <ReviewFile label="Passport photograph" file={data.passportPhoto} />
          <ReviewFile label="Curriculum Vitae" file={data.cvFile} />
          <ReviewFile label="Identification document" file={data.idDocument} />
          {data.additionalDocuments.map((file, index) => <ReviewFile key={`${file.name}-${index}`} label={`Additional document ${index + 1}`} file={file} />)}
          {!data.additionalDocuments.length && <ReviewFile label="Additional supporting documents" file={null} />}
        </div>
        <ReviewDetails items={[["ID type (optional)", data.idType], ["ID number (optional)", data.idNumber]]} />
        <p className="fdcCareerAppReviewHint">Documents are submitted for verification only when you confirm the application.</p>
      </ReviewSection>

      <ReviewSection icon={WorkRounded} title="Preferred work location" subtitle="Your primary state preference" step={5} onEdit={onEdit}>
        <div className="fdcCareerAppReviewLocation"><LocationOnRounded /><div><small>YOUR SELECTED STATE</small><strong>{reviewText(data.assignedState)}</strong><p>This is a preference and does not guarantee assignment.</p></div></div>
      </ReviewSection>

      <ReviewSection icon={ShieldRounded} title="Application status" subtitle="Your application is submitted for recruitment review" step={6} onEdit={onEdit}>
        <div className="fdcCareerAppReviewSecurity"><InfoOutlined /><div><strong>Approval is required</strong><p>Submitting an application does not create an active FDC account. Access is subject to recruitment review and approval.</p></div></div>
        <ReviewDetails items={[["Application status", "Pending recruitment review"], ["Application channel", "Universe Careers portal"]]} />
      </ReviewSection>
    </div>
  );
}

function Heading({ title, text }) {
  return (
    <header className="fdcCareerAppOnboardHeading">
      <h3>{title}</h3>
      <p>{text}</p>
    </header>
  );
}
function Grid({ children }) {
  return <div className="fdcCareerAppOnboardGrid">{children}</div>;
}
function Field({ label, error, children }) {
  return (
    <label className={`fdcCareerAppOnboardField ${error ? "error" : ""}`}>
      <span>{label}</span>
      {children}
      {error && <small>{error}</small>}
    </label>
  );
}
function PublicInfo({ activeStaff }) {
  return (
    <>
      <LocationOnRounded />
      <div>
        <small>EXECUTIVE JURISDICTION</small>
        <strong>
          {activeStaff?.territory?.regionName || "Assigned region"}
        </strong>
        <span>
          {activeStaff?.territory?.totalStates || 0} states available for
          assignment
        </span>
      </div>
    </>
  );
}
